import { useEffect, useState } from 'react';
import {
    Loader2, Save, Send, ShieldCheck, AlertTriangle, Server, Info,
    RotateCcw, CheckCircle2, KeyRound, Eye, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import * as apiClient from '@/services/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ConfirmDialog from '@/components/ux/ConfirmDialog';
import { cn } from '@/lib/utils';

/**
 * Per-company SMTP setup.
 *
 * The flow is deliberately two-step — save, then verify — because only a
 * verified configuration is used for real candidate email. The UI leads with
 * that status so it is always obvious which mail server is currently sending.
 */

const STATUS_META = {
    unconfigured: {
        label: 'Using EchoHire mail server',
        tone:  'bg-muted text-muted-foreground border-border',
        icon:  Server,
        blurb: 'Candidate emails are sent from EchoHire. Add your own server below to send from your domain.',
    },
    unverified: {
        label: 'Saved — not yet verified',
        tone:  'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25',
        icon:  AlertTriangle,
        blurb: 'Your settings are stored but not in use yet. Send a test email to activate them.',
    },
    verified: {
        label: 'Active — sending from your server',
        tone:  'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25',
        icon:  ShieldCheck,
        blurb: 'Candidate emails are being sent from your mail server.',
    },
    failing: {
        label: 'Not working — using EchoHire mail server',
        tone:  'bg-destructive/10 text-destructive border-destructive/25',
        icon:  AlertTriangle,
        blurb: 'Your mail server rejected a recent message, so emails are going out from EchoHire until this is fixed.',
    },
};

const PRESETS = {
    gmail:     { label: 'Gmail / Google Workspace', host: 'smtp.gmail.com',        port: 587, secure: false },
    outlook:   { label: 'Outlook / Microsoft 365',  host: 'smtp.office365.com',    port: 587, secure: false },
    sendgrid:  { label: 'SendGrid',                 host: 'smtp.sendgrid.net',     port: 587, secure: false },
    ses:       { label: 'Amazon SES (Mumbai)',      host: 'email-smtp.ap-south-1.amazonaws.com', port: 587, secure: false },
    zoho:      { label: 'Zoho Mail',                host: 'smtp.zoho.in',          port: 465, secure: true  },
};

const EMPTY = {
    host: '', port: 587, secure: false, authUser: '',
    password: '', fromName: '', fromEmail: '', replyTo: '',
};

export default function EmailSetupSection() {
    const [form,    setForm]    = useState(EMPTY);
    const [status,  setStatus]  = useState('unconfigured');
    const [meta,    setMeta]    = useState({});
    const [canStore, setCanStore] = useState(true);

    const [loading,  setLoading]  = useState(true);
    const [saving,   setSaving]   = useState(false);
    const [testing,  setTesting]  = useState(false);
    const [dirty,    setDirty]    = useState(false);
    const [showPass, setShowPass] = useState(false);
    const [testTo,   setTestTo]   = useState('');
    const [revertOpen, setRevertOpen] = useState(false);

    const load = async () => {
        try {
            const res = await apiClient.getCompanyEmailConfig();
            const d = res.data || {};
            setForm({
                host: d.host || '', port: d.port ?? 587, secure: Boolean(d.secure),
                authUser: d.authUser || '', password: '',
                fromName: d.fromName || '', fromEmail: d.fromEmail || '', replyTo: d.replyTo || '',
            });
            setStatus(d.status || 'unconfigured');
            setMeta({ hasPassword: d.hasPassword, lastTestedAt: d.lastTestedAt, lastError: d.lastError });
            setCanStore(d.encryptionAvailable !== false);
            setDirty(false);
        } catch (err) {
            toast.error(err.message || 'Could not load email settings.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const setField = (key) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm(prev => ({ ...prev, [key]: value }));
        setDirty(true);
    };

    const applyPreset = (key) => {
        const p = PRESETS[key];
        if (!p) return;
        setForm(prev => ({ ...prev, host: p.host, port: p.port, secure: p.secure }));
        setDirty(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await apiClient.updateCompanyEmailConfig(form);
            setStatus(res.data.status);
            setMeta(m => ({ ...m, hasPassword: res.data.hasPassword }));
            setForm(prev => ({ ...prev, password: '' }));   // never keep it in memory
            setDirty(false);
            toast.success(res.message);
        } catch (err) {
            toast.error(err.message || 'Could not save email settings.');
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        try {
            const res = await apiClient.testCompanyEmailConfig(testTo.trim() || undefined);
            setStatus(res.data.status);
            setMeta(m => ({ ...m, lastTestedAt: res.data.lastTestedAt, lastError: '' }));
            toast.success(res.message);
        } catch (err) {
            setStatus('failing');
            setMeta(m => ({ ...m, lastError: err.message }));
            toast.error(err.message || 'Test email failed.');
        } finally {
            setTesting(false);
        }
    };

    const handleRevert = async () => {
        try {
            const res = await apiClient.deleteCompanyEmailConfig();
            setForm(EMPTY);
            setStatus(res.data.status);
            setMeta({});
            setDirty(false);
            setRevertOpen(false);
            toast.success(res.message);
        } catch (err) {
            toast.error(err.message || 'Could not revert.');
        }
    };

    if (loading) {
        return (
            <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-11 animate-pulse rounded-lg bg-muted/50" />
                ))}
            </div>
        );
    }

    const s = STATUS_META[status] || STATUS_META.unconfigured;
    const canTest = Boolean(form.host && form.authUser && (meta.hasPassword || form.password)) && !dirty;

    return (
        <div className="space-y-8">
            {/* ── Status ────────────────────────────────────────────────── */}
            <div className={cn('flex items-start gap-3 rounded-xl border px-4 py-3.5', s.tone)}>
                <s.icon className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0 space-y-0.5">
                    <p className="text-sm font-semibold">{s.label}</p>
                    <p className="text-xs opacity-90">{s.blurb}</p>
                    {status === 'failing' && meta.lastError && (
                        <p className="pt-1 text-xs font-medium">Last error: {meta.lastError}</p>
                    )}
                    {status === 'verified' && meta.lastTestedAt && (
                        <p className="pt-1 text-xs opacity-75">
                            Verified {new Date(meta.lastTestedAt).toLocaleString()}
                        </p>
                    )}
                </div>
            </div>

            {!canStore && (
                <div className="flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3.5 py-3 text-sm text-amber-700 dark:text-amber-400">
                    <KeyRound className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                        The server is missing its encryption key, so mail credentials cannot be stored yet.
                        Ask your administrator to set <code className="font-mono text-xs">SETTINGS_ENCRYPTION_KEY</code>.
                    </span>
                </div>
            )}

            {/* ── Server ────────────────────────────────────────────────── */}
            <section className="space-y-4">
                <div>
                    <h3 className="text-sm font-semibold text-foreground">Mail server</h3>
                    <p className="text-xs text-muted-foreground">
                        Where your candidate emails are sent from. Pick a provider to fill this in.
                    </p>
                </div>

                <div className="flex flex-wrap gap-1.5">
                    {Object.entries(PRESETS).map(([k, p]) => (
                        <button key={k} type="button" onClick={() => applyPreset(k)} disabled={!canStore}
                            className="rounded-full border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground disabled:opacity-50">
                            {p.label}
                        </button>
                    ))}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5 sm:col-span-2">
                        <Label htmlFor="smtp-host">SMTP host <span className="text-destructive">*</span></Label>
                        <Input id="smtp-host" value={form.host} onChange={setField('host')}
                            placeholder="smtp.yourcompany.com" className="h-11" disabled={!canStore} />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="smtp-port">Port <span className="text-destructive">*</span></Label>
                        <Input id="smtp-port" type="number" value={form.port} onChange={setField('port')}
                            className="h-11" disabled={!canStore} />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="smtp-secure">Security</Label>
                        <select id="smtp-secure" value={form.secure ? 'ssl' : 'starttls'} disabled={!canStore}
                            onChange={e => { setForm(p => ({ ...p, secure: e.target.value === 'ssl' })); setDirty(true); }}
                            className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                            <option value="starttls">STARTTLS — usually port 587</option>
                            <option value="ssl">SSL/TLS — usually port 465</option>
                        </select>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="smtp-user">Username <span className="text-destructive">*</span></Label>
                        <Input id="smtp-user" value={form.authUser} onChange={setField('authUser')}
                            placeholder="you@yourcompany.com" autoComplete="off" className="h-11" disabled={!canStore} />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="smtp-pass">
                            Password {meta.hasPassword
                                ? <span className="font-normal text-muted-foreground">(leave blank to keep current)</span>
                                : <span className="text-destructive">*</span>}
                        </Label>
                        <div className="relative">
                            <Input id="smtp-pass" type={showPass ? 'text' : 'password'}
                                value={form.password} onChange={setField('password')}
                                placeholder={meta.hasPassword ? '••••••••••••' : 'App password'}
                                autoComplete="new-password" className="h-11 pr-10" disabled={!canStore} />
                            <button type="button" onClick={() => setShowPass(v => !v)} tabIndex={-1}
                                aria-label={showPass ? 'Hide password' : 'Show password'}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-3.5 py-2.5">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                        Gmail and Microsoft 365 reject normal account passwords over SMTP — create an
                        <strong className="text-foreground"> app password</strong> instead. Your password is
                        encrypted before it is stored and is never shown again after saving.
                    </p>
                </div>
            </section>

            {/* ── Sender identity ───────────────────────────────────────── */}
            <section className="space-y-4">
                <div>
                    <h3 className="text-sm font-semibold text-foreground">Sender identity</h3>
                    <p className="text-xs text-muted-foreground">How your name and address appear in a candidate&rsquo;s inbox.</p>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="from-name">From name</Label>
                        <Input id="from-name" value={form.fromName} onChange={setField('fromName')}
                            placeholder="Acme Hiring Team" className="h-11" disabled={!canStore} />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="from-email">From address</Label>
                        <Input id="from-email" type="email" value={form.fromEmail} onChange={setField('fromEmail')}
                            placeholder="careers@acme.com" className="h-11" disabled={!canStore} />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                        <Label htmlFor="reply-to">
                            Reply-to <span className="font-normal text-muted-foreground">(optional)</span>
                        </Label>
                        <Input id="reply-to" type="email" value={form.replyTo} onChange={setField('replyTo')}
                            placeholder="Where candidate replies should go" className="h-11" disabled={!canStore} />
                    </div>
                </div>
            </section>

            {/* ── Verify ────────────────────────────────────────────────── */}
            <section className="space-y-3 rounded-xl border border-border bg-muted/20 p-4">
                <div>
                    <h3 className="text-sm font-semibold text-foreground">Verify</h3>
                    <p className="text-xs text-muted-foreground">
                        Settings go live only after a test email arrives — that way a typo can never
                        stop candidates hearing from you.
                    </p>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[14rem] flex-1 space-y-1.5">
                        <Label htmlFor="test-to" className="text-xs">Send test to</Label>
                        <Input id="test-to" type="email" value={testTo} onChange={e => setTestTo(e.target.value)}
                            placeholder="your own address" className="h-9" />
                    </div>
                    <Button variant="outline" onClick={handleTest} disabled={!canTest || testing} className="h-9 gap-1.5">
                        {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        Send test email
                    </Button>
                </div>
                {dirty && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400">
                        Save your changes before sending a test.
                    </p>
                )}
            </section>

            {/* ── Actions ───────────────────────────────────────────────── */}
            <div className="sticky bottom-0 -mx-1 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-background/95 px-1 py-3 backdrop-blur">
                {status !== 'unconfigured' ? (
                    <Button variant="ghost" size="sm"
                        className="gap-1.5 text-muted-foreground hover:text-destructive"
                        onClick={() => setRevertOpen(true)}>
                        <RotateCcw className="h-3.5 w-3.5" />Use EchoHire mail server
                    </Button>
                ) : <span />}

                <div className="flex items-center gap-2">
                    {!dirty && status === 'verified' && (
                        <span className="flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="h-3 w-3" />Active
                        </span>
                    )}
                    <Button onClick={handleSave} disabled={saving || !dirty || !canStore} className="gap-1.5">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Save settings
                    </Button>
                </div>
            </div>

            <ConfirmDialog
                open={revertOpen}
                onOpenChange={setRevertOpen}
                title="Use the EchoHire mail server?"
                description="Your stored SMTP settings and password will be deleted, and candidate emails will be sent from EchoHire again. You can set your own server up again at any time."
                confirmLabel="Revert"
                variant="destructive"
                onConfirm={handleRevert}
            />
        </div>
    );
}
